import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Users, GraduationCap, TrendingUp, AlertTriangle, IndianRupee, Trophy, RefreshCw } from "lucide-react";

const CHART_COLORS = ["#38BDF8", "#F97316", "#22C55E", "#A78BFA", "#EF4444", "#EAB308", "#EC4899", "#14B8A6", "#F59E0B"];

function Kpi({ label, value, icon: Icon, hint, tone = "blue" }) {
  const toneMap = {
    blue: "brand-blue",
    orange: "brand-orange",
    green: "text-emerald-400",
    red: "text-red-400",
  };
  return (
    <div className="border border-white/12 bg-[#131A2B] p-5" data-testid={`ana-kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{label}</div>
        <Icon size={14} className="text-white/40" />
      </div>
      <div className={`font-display text-3xl font-black mt-1 ${toneMap[tone]}`}>{value}</div>
      {hint && <div className="text-[10px] font-mono text-white/40 mt-1">{hint}</div>}
    </div>
  );
}

const TooltipDark = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0B1120] border border-white/20 p-3 text-xs">
      <div className="font-mono text-white/60 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2" style={{ background: p.color }} />
          <span className="text-white/80">{p.name}</span>
          <span className="brand-blue font-display">{typeof p.value === "number" ? p.value.toLocaleString("en-IN") : p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function AdminAnalytics() {
  const [kpis, setKpis] = useState(null);
  const [rev, setRev] = useState([]);
  const [att, setAtt] = useState([]);
  const [attByBatch, setAttByBatch] = useState([]);
  const [mix, setMix] = useState([]);
  const [aging, setAging] = useState(null);
  const [monthsWindow, setMonthsWindow] = useState(6);
  const [daysWindow, setDaysWindow] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [k, r, a, ab, m, ag] = await Promise.all([
        api.get("/admin/analytics/dashboard-kpis"),
        api.get(`/admin/analytics/revenue-mom?months=${monthsWindow}`),
        api.get(`/admin/analytics/attendance-trend?days=${daysWindow}`),
        api.get(`/admin/analytics/attendance-by-batch?days=${daysWindow}`),
        api.get(`/admin/analytics/service-mix?days=${daysWindow * 3}`),
        api.get("/admin/analytics/dues-aging"),
      ]);
      setKpis(k.data); setRev(r.data); setAtt(a.data); setAttByBatch(ab.data); setMix(m.data);
      setAging(ag.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthsWindow, daysWindow]);

  const totalRevenue = rev.reduce((s, r) => s + r.total, 0);
  const avgAttendance = att.filter((d) => d.rate != null).reduce((s, d, _, arr) => s + d.rate / arr.length, 0);

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Analytics · Insights</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Command Insights.</h1>
          <p className="text-sm text-white/60 mt-2 max-w-xl">Revenue trends, attendance signals and service mix — updated in real-time from your operational data.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={monthsWindow} onChange={(e) => setMonthsWindow(parseInt(e.target.value))}
            data-testid="ana-months"
            className="bg-[#0B1120] border border-white/20 py-2 px-3 text-sm">
            {[3, 6, 9, 12].map((n) => <option key={n} value={n}>{n} mo revenue</option>)}
          </select>
          <select value={daysWindow} onChange={(e) => setDaysWindow(parseInt(e.target.value))}
            data-testid="ana-days"
            className="bg-[#0B1120] border border-white/20 py-2 px-3 text-sm">
            {[14, 30, 60, 90].map((n) => <option key={n} value={n}>{n} days trends</option>)}
          </select>
          <button onClick={load} data-testid="ana-refresh"
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-brand-blue text-white px-4 py-2 hover:glow-blue">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Kpi label="Active parents" value={kpis.active_parents} icon={Users} tone="blue" />
          <Kpi label="Active wards" value={kpis.active_wards} icon={GraduationCap} tone="blue" />
          <Kpi label="Fees this month" value={formatINR(kpis.fees_collected_month)} icon={IndianRupee} tone="green" />
          <Kpi label="Bookings revenue" value={formatINR(kpis.bookings_revenue_month)} icon={TrendingUp} tone="green" />
          <Kpi label="Outstanding" value={kpis.outstanding_invoices} icon={AlertTriangle} tone="orange" hint="Unpaid invoices" />
          <Kpi label="Upcoming tournaments" value={kpis.upcoming_tournaments} icon={Trophy} tone="orange" />
          <Kpi label={`Avg attendance (${daysWindow}d)`} value={`${avgAttendance.toFixed(1)}%`} icon={Users} tone="blue" />
          <Kpi label={`Revenue ${monthsWindow}mo`} value={formatINR(totalRevenue)} icon={IndianRupee} tone="green" />
        </div>
      )}

      {loading && <div className="text-white/40 py-8 text-center">Loading charts…</div>}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        {/* Revenue MoM */}
        <div className="border border-white/12 bg-[#131A2B] p-6" data-testid="ana-chart-revenue">
          <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-4">// Revenue Month-over-Month</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={rev}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="month" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip content={<TooltipDark />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="invoices_amount" name="Fees" stackId="a" fill="#38BDF8" />
              <Bar dataKey="bookings_amount" name="Bookings" stackId="a" fill="#F97316" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Attendance trend */}
        <div className="border border-white/12 bg-[#131A2B] p-6" data-testid="ana-chart-attendance">
          <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-4">// Daily attendance rate</div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={att}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#64748B" fontSize={11} domain={[0, 100]} unit="%" />
              <Tooltip content={<TooltipDark />} />
              <Line type="monotone" dataKey="rate" name="Rate %" stroke="#38BDF8" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Attendance by batch */}
        <div className="border border-white/12 bg-[#131A2B] p-6" data-testid="ana-chart-att-by-batch">
          <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-4">// Attendance by batch</div>
          {attByBatch.length === 0 ? (
            <div className="text-white/40 text-sm py-8">No attendance data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={attByBatch} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis type="number" stroke="#64748B" fontSize={11} domain={[0, 100]} unit="%" />
                <YAxis dataKey="batch_name" type="category" stroke="#64748B" fontSize={10} width={110} />
                <Tooltip content={<TooltipDark />} />
                <Bar dataKey="rate" name="Rate %" fill="#22C55E" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Service mix */}
        <div className="border border-white/12 bg-[#131A2B] p-6" data-testid="ana-chart-service-mix">
          <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-4">// Service revenue mix</div>
          {mix.length === 0 ? (
            <div className="text-white/40 text-sm py-8">No bookings in window.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={mix}
                  dataKey="amount"
                  nameKey="service"
                  cx="50%" cy="50%"
                  outerRadius={100}
                  labelLine={false}
                  label={({ service, percent }) => percent > 0.04 ? `${service} · ${(percent * 100).toFixed(0)}%` : ""}
                  fontSize={10}
                >
                  {mix.map((entry, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<TooltipDark />} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* DUES AGING — slow-payer alert table */}
      {aging && (aging.grand_count_overdue > 0 || (aging.buckets?.not_due?.count || 0) > 0) && (
        <div className="mt-8 border border-white/12 bg-[#131A2B] p-6" data-testid="dues-aging">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Dues Aging</div>
              <h3 className="font-display text-xl font-black uppercase mt-1">Slow payers.</h3>
              <p className="text-xs text-white/50 mt-1">
                {aging.grand_count_overdue} overdue row(s) · <b className="text-red-300">₹{Number(aging.grand_total_overdue || 0).toLocaleString("en-IN")}</b> outstanding as of {aging.as_of}
              </p>
            </div>
            <button
              type="button"
              data-testid="ping-overdue-btn"
              onClick={async () => {
                try {
                  const r = await api.post("/admin/analytics/dues-aging/ping-overdue");
                  toast.success(`WhatsApp sent: ${r.data.pinged}/${r.data.checked}`);
                } catch (e) { toast.error(apiError(e)); }
              }}
              className="inline-flex items-center gap-2 bg-brand-orange text-black px-4 py-2 font-bold text-xs uppercase tracking-widest hover:bg-white"
            >
              ↗ Ping overdue on WhatsApp
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {["0-30", "31-60", "61-90", "91+"].map((k) => {
              const b = aging.buckets?.[k] || { count: 0, total: 0 };
              const tone = k === "91+" ? "border-red-500 text-red-300 bg-red-500/10"
                : k === "61-90" ? "border-orange-500 text-orange-300 bg-orange-500/10"
                : k === "31-60" ? "border-yellow-500 text-yellow-300 bg-yellow-500/10"
                : "border-white/20 text-white/70 bg-white/5";
              return (
                <div key={k} className={`border p-3 ${tone}`} data-testid={`aging-bucket-${k}`}>
                  <div className="font-mono text-[10px] uppercase tracking-widest opacity-80">Days late · {k}</div>
                  <div className="font-display text-3xl font-black mt-1">{b.count}</div>
                  <div className="font-mono text-[11px] mt-0.5">₹{Number(b.total || 0).toLocaleString("en-IN")}</div>
                </div>
              );
            })}
          </div>

          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/5">
                <tr className="text-left font-mono uppercase text-[10px] tracking-widest text-white/50">
                  <th className="p-2">Parent</th>
                  <th className="p-2">Ward</th>
                  <th className="p-2">Description</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2 text-right">Paid</th>
                  <th className="p-2 text-right">Balance</th>
                  <th className="p-2 text-right">Due date</th>
                  <th className="p-2 text-right">Days late</th>
                </tr>
              </thead>
              <tbody>
                {["91+", "61-90", "31-60", "0-30"].flatMap((k) => (aging.buckets?.[k]?.rows || []).map((r) => (
                  <tr key={r.id} className="border-t border-white/8">
                    <td className="p-2">{r.parent_name || "-"}</td>
                    <td className="p-2 text-white/70">{r.ward_name || "-"}</td>
                    <td className="p-2 text-white/70">{r.description || "-"}</td>
                    <td className="p-2 text-right font-mono">₹{Number(r.total).toLocaleString("en-IN")}</td>
                    <td className="p-2 text-right font-mono text-green-300">₹{Number(r.amount_paid).toLocaleString("en-IN")}</td>
                    <td className="p-2 text-right font-mono brand-orange font-bold">₹{Number(r.balance).toLocaleString("en-IN")}</td>
                    <td className="p-2 text-right text-white/50">{r.due_date}</td>
                    <td className={`p-2 text-right font-bold ${r.days_late > 90 ? "text-red-300" : r.days_late > 60 ? "text-orange-300" : r.days_late > 30 ? "text-yellow-300" : "text-white/70"}`}>
                      {r.days_late}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
